import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExportOutlined,
  ReloadOutlined,
  ScanOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Result, Spin, Typography } from "antd";
import styled from "@emotion/styled";
import { QRCodeCanvas } from "qrcode.react";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ManageParcelPageHeader from "components/ManageParcel/ParcelShared/ManageParcelPageHeader";
import { CREDIT_BALANCE_QUERY_KEY, useFactoryCodCreditInfo } from "hooks/useFactoryCredit";
import { CREDIT_TRANSACTIONS_QUERY_KEY } from "hooks/useFactoryCreditTransactions";
import { CREDIT_TOPUPS_QUERY_KEY } from "hooks/useFactoryCreditTopups";
import {
  CreditTopupStatus,
  TopupInitiateResult,
  getFactoryCreditTopupStatus,
  initiateFactoryCreditTopup,
} from "services/factoryCreditApi";

const { Text } = Typography;

const numberFormatTHB = (n: number) => n.toLocaleString("th-TH", { minimumFractionDigits: 0 });

/**
 * ซ้าย = รายละเอียดคำสั่งซื้อ (60%), ขวา = QR ชำระเงิน (40%) — จอเล็ก (≤900px) ยุบเป็นคอลัมน์เดียว
 * (รายละเอียดอยู่บน, QR ตกลงมาอยู่ล่าง) ตามแบบเดียวกับ SplitLayout ของ CreditTopupPage.tsx
 */
const CheckoutLayout = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 24px;
  align-items: flex-start;
  margin-top: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/** ย้ายมาจาก PaymentQrModal.tsx เดิม (ไม่เปลี่ยน logic) — tick ทุกวินาที คำนวณจาก timestamp จริงกัน
 * drift สะสม */
const QrCountdown: FC<{ expiresAt: number }> = ({ expiresAt }) => {
  const [remainingMs, setRemainingMs] = useState(() => expiresAt - Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setRemainingMs(expiresAt - Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);

  const clampedSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const mm = String(Math.floor(clampedSeconds / 60)).padStart(2, "0");
  const ss = String(clampedSeconds % 60).padStart(2, "0");
  const expired = clampedSeconds <= 0;

  return (
    <Text strong style={{ color: "#ff4d4f", fontSize: 15 }}>
      {expired ? "ชำระเงินไม่สำเร็จ — กรุณาเริ่มรายการใหม่" : `โปรดชำระเงินภายใน ${mm}:${ss} นาที`}
    </Text>
  );
};

/**
 * 2026-09-08: หน้า checkout เต็มหน้าสำหรับเติมเครดิต (แทนที่ PaymentQrModal เดิมทั้ง 2 จุดที่เคยเปิดใช้ —
 * CreditTopupPage.tsx (กด "ยืนยันการชำระเงิน" ในหน้าเลือกแพ็กเกจ) และ CreditTopupListTable.tsx (กด
 * "คลิกเพื่อชำระเงิน" บนแถว PENDING ในตาราง "ประวัติการใช้เครดิต") — ตามที่ขอให้เป็นหน้า checkout ดีไซน์
 * ซ้าย-ขวาแบบเดียวกับหน้า checkout ของ bangbow-ecommerce-web (ซ้าย = รายละเอียดคำสั่งซื้อ ไม่มีที่อยู่
 * เพราะเติมเครดิตไม่มีการจัดส่ง, ขวา = QR ชำระเงิน) แทน modal เดิม
 *
 * รับ packageVersionId ผ่าน query string (?packageVersionId=...) ไม่ผ่าน prop — เรียก
 * initiateFactoryCreditTopup เองทันทีที่ mount (resume อัตโนมัติถ้ามี PENDING เดิมยังไม่หมดอายุอยู่ ดู
 * docstring ของ FactoryCreditService.initiateFactoryTopUp ฝั่ง API) แล้ว poll สถานะทุก 3 วิ — logic
 * เดิมทั้งหมดย้ายมาจาก PaymentQrModal.tsx (ไฟล์นั้นเลิกใช้แล้ว แต่ยังไม่ได้ลบไว้เผื่อย้อนกลับ)
 *
 * QR: ถ้า charge.qr_pay_content มีค่า (ดึงจาก Ksher /api/pay สำเร็จ — ดู
 * PaymentService.fetchKsherQrContent ฝั่ง API) render QR จริงในหน้านี้เลย (data:image → <img>, EMVCo
 * string ดิบ → <QRCodeCanvas>) — ถ้าไม่มี (endpoint นั้นล้มเหลว) fallback เปิดแท็บใหม่ไป qr_content
 * (checkout URL ของ Ksher) อัตโนมัติเหมือนพฤติกรรมเดิมก่อนหน้านี้
 */
const CreditTopupCheckoutPage: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const packageVersionId = searchParams.get("packageVersionId");

  // 2026-09-08: "เครดิตที่จะได้รับ" ต้องคูณ cod_credit_multiplier เหมือนกับที่การ์ดเลือกแพ็กเกจ
  // (CreditTopupPage.tsx PackageCard) โชว์ไว้ตอนเลือก — ไม่งั้นตัวเลขที่เห็นตอนเลือกกับตอน checkout จะ
  // ไม่ตรงกัน (ยอดที่ได้รับจริงในบัญชี = credit_amount ดิบ ไม่ได้คูณ — ตัวคูณนี้มีผลแค่ตอนคำนวณโควต้า COD
  // ที่เครดิตนี้หนุนได้ ไม่ใช่จำนวนเครดิตที่เพิ่มเข้าบัญชีจริง แต่หน้าจอทั้ง 2 หน้าต้องโชว์เลขเดียวกัน)
  const { data: codCreditInfo } = useFactoryCodCreditInfo();
  const multiplier = codCreditInfo?.cod_credit_multiplier ?? 2;

  const [charge, setCharge] = useState<TopupInitiateResult | null>(null);
  const [initiating, setInitiating] = useState(false);
  const [initiateError, setInitiateError] = useState<string | null>(null);
  /** กันเปิดแท็บซ้ำ — auto-open ครั้งเดียวต่อ order_code เดียว (เฉพาะตอนไม่มี QR จริงให้โชว์) */
  const autoOpenedOrderCodeRef = useRef<string | null>(null);

  const runInitiate = () => {
    if (!packageVersionId) return;
    setCharge(null);
    setInitiateError(null);
    setInitiating(true);
    initiateFactoryCreditTopup(packageVersionId)
      .then((result) => setCharge(result))
      .catch((e: any) => setInitiateError(e?.message || "สร้างรายการชำระเงินไม่สำเร็จ กรุณาลองใหม่"))
      .finally(() => setInitiating(false));
  };

  useEffect(() => {
    runInitiate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageVersionId]);

  // fallback: เปิดแท็บใหม่อัตโนมัติเฉพาะตอนไม่มี qr_pay_content (ดึง QR จริงจาก Ksher ไม่สำเร็จ)
  useEffect(() => {
    if (
      charge?.order_code &&
      !charge.qr_pay_content &&
      autoOpenedOrderCodeRef.current !== charge.order_code
    ) {
      autoOpenedOrderCodeRef.current = charge.order_code;
      window.open(charge.qr_content, "_blank", "noopener");
    }
  }, [charge]);

  const { data: status, refetch: refetchStatus, isFetching: statusFetching } = useQuery<
    CreditTopupStatus | null,
    Error
  >({
    queryKey: ["factoryCreditTopupStatus", charge?.order_code],
    queryFn: () => getFactoryCreditTopupStatus(charge!.order_code),
    enabled: !!charge?.order_code,
    refetchInterval: (query) => (query.state.data?.topup_status === "PENDING" ? 3000 : false),
  });

  // ผลจ่ายมาแล้ว (COMPLETED) — รีเฟรชการ์ด/ประวัติ/ตารางจัดการเครดิตทันที ไม่ต้องรอ websocket reload
  useEffect(() => {
    if (status?.topup_status === "COMPLETED") {
      queryClient.refetchQueries({ queryKey: [CREDIT_BALANCE_QUERY_KEY] });
      queryClient.refetchQueries({ queryKey: [CREDIT_TRANSACTIONS_QUERY_KEY] });
      queryClient.refetchQueries({ queryKey: [CREDIT_TOPUPS_QUERY_KEY] });
    }
  }, [status?.topup_status, queryClient]);

  const goToHistory = () => navigate("/manage-credit");

  // จ่ายสำเร็จแล้วกลับไปหน้าประวัติอัตโนมัติ (ดีเลย์สั้นๆ ให้เห็นข้อความสำเร็จก่อน)
  useEffect(() => {
    if (status?.topup_status !== "COMPLETED") return;
    const timer = window.setTimeout(goToHistory, 1500);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.topup_status]);

  const handleBack = () => {
    if (window.history.state && typeof window.history.state.idx === "number" && window.history.state.idx > 0) {
      navigate(-1);
      return;
    }
    navigate("/manage-credit");
  };

  const renderOrderDetail = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
      <Card title="รายละเอียดคำสั่งซื้อ" style={{ borderRadius: 12 }}>
        <div style={{ padding: 24 }}>
          {!charge ? (
            <Text type="secondary">กำลังสร้างรายการ...</Text>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <Text style={{ color: "rgba(0,0,0,0.85)" }}>เลขที่คำสั่งซื้อ</Text>
                <Text strong>{charge.order_code}</Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <Text style={{ color: "rgba(0,0,0,0.85)" }}>เครดิตที่จะได้รับ</Text>
                <Text strong>{numberFormatTHB(charge.credit_amount * multiplier)} เครดิต</Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <Text style={{ color: "rgba(0,0,0,0.85)" }}>อายุการใช้งาน</Text>
                <Text strong>{charge.expiry_days} วัน</Text>
              </div>
              <div style={{ borderTop: "1px solid #f0f0f0", margin: "12px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text strong style={{ fontSize: 15 }}>
                  ยอดที่ต้องชำระ
                </Text>
                <Text strong style={{ fontSize: 22, color: "#1677ff" }}>
                  {Number(charge.price_thb).toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท
                </Text>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* 2026-09-08: การ์ดวิธีชำระเงินตามที่ขอ — ขั้นตอนที่ 5 มีข้อความเตือนสีแดงต่อท้าย */}
      <Card title="วิธีชำระเงิน" style={{ borderRadius: 12 }}>
        <div style={{ padding: 24 }}>
          <ol style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 10 }}>
            <li>
              <Text>เปิดแอปธนาคารบนสมาร์ทโฟนของคุณ</Text>
            </li>
            <li>
              <Text>เลือกตัวเลือกชำระเงินด้วยคิวอาร์โค้ด</Text>
            </li>
            <li>
              <Text>สแกนคิวอาร์โค้ดด้านขวามือ</Text>
            </li>
            <li>
              <Text>ตรวจสอบรายละเอียดและยืนยันการชำระเงิน</Text>
            </li>
            <li>
              <Text>รอให้ระบบประมวลผลธุรกรรม </Text>
              <Text strong style={{ color: "#ff4d4f" }}>
                โปรดอย่าเปลี่ยนหรือปิดหน้าจอนี้
              </Text>
            </li>
          </ol>
        </div>
      </Card>
    </div>
  );

  const renderPaymentPanel = () => {
    if (initiating && !charge) {
      return (
        <Card title="สแกน QR เพื่อชำระเงิน" style={{ borderRadius: 12, minWidth: 0 }}>
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spin size="large" />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">กำลังสร้างรายการชำระเงิน...</Text>
            </div>
          </div>
        </Card>
      );
    }

    if (initiateError) {
      return (
        <Card style={{ borderRadius: 12, minWidth: 0 }}>
          <Result
            icon={<CloseCircleFilled style={{ color: "#ff4d4f" }} />}
            status="error"
            title="สร้างรายการชำระเงินไม่สำเร็จ"
            subTitle={initiateError}
            extra={
              <Button type="primary" onClick={runInitiate}>
                ลองใหม่
              </Button>
            }
          />
        </Card>
      );
    }

    if (!charge) return null;

    if (status?.topup_status === "COMPLETED") {
      return (
        <Card style={{ borderRadius: 12, minWidth: 0 }}>
          <Result
            icon={<CheckCircleFilled style={{ color: "#52c41a" }} />}
            status="success"
            title="เติมเครดิตสำเร็จ"
            subTitle={`ได้รับเครดิต ${numberFormatTHB(status.credit_amount ?? charge.credit_amount)} เข้าบัญชีแล้ว`}
            extra={
              <Button type="primary" onClick={goToHistory}>
                ไปที่ประวัติการใช้เครดิต
              </Button>
            }
          />
        </Card>
      );
    }

    if (status?.topup_status === "FAILED" || status?.topup_status === "EXPIRED") {
      return (
        <Card style={{ borderRadius: 12, minWidth: 0 }}>
          <Result
            icon={<CloseCircleFilled style={{ color: "#ff4d4f" }} />}
            status="error"
            title="ชำระเงินไม่สำเร็จ"
            subTitle="กรุณาลองเติมเครดิตอีกครั้ง"
            extra={
              <Button type="primary" onClick={runInitiate}>
                ลองใหม่
              </Button>
            }
          />
        </Card>
      );
    }

    const hasRealQr = !!charge.qr_pay_content;

    return (
      <Card
        title="สแกน QR เพื่อชำระเงิน"
        style={{ borderRadius: 12, minWidth: 0 }}
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            <SyncOutlined spin /> กำลังตรวจสอบสถานะอัตโนมัติ
          </Text>
        }
      >
        <div style={{ padding: 24, textAlign: "center" }}>
          <Text strong style={{ color: "#ff4d4f", display: "block", marginBottom: 16 }}>
            โปรดทำการชำระเงินเพียงครั้งเดียว!
          </Text>

          {hasRealQr ? (
            <>
              <div
                style={{
                  border: "1px solid #e6e6e6",
                  borderRadius: 12,
                  padding: 20,
                  display: "inline-block",
                }}
              >
                <div style={{ marginBottom: 12 }}>
                  <img src="/PromptPay-logo.png" alt="PromptPay" style={{ height: 32 }} />
                </div>
                {charge.qr_pay_content!.startsWith("data:image") ? (
                  <img src={charge.qr_pay_content!} alt="QR ชำระเงิน" width={240} height={240} />
                ) : (
                  <QRCodeCanvas value={charge.qr_pay_content!} size={240} />
                )}
              </div>
              <div style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <ScanOutlined style={{ color: "#8c8c8c" }} />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  โปรดใช้แอปธนาคาร / PromptPay สแกนเพื่อชำระเงิน
                </Text>
              </div>
            </>
          ) : (
            <div style={{ padding: "24px 0" }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text strong style={{ display: "block" }}>เปิดหน้าชำระเงินในแท็บใหม่ให้แล้ว</Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ถ้าไม่มีแท็บใหม่เปิดขึ้นมา (เบราว์เซอร์บล็อก popup) กดปุ่มด้านล่างเพื่อเปิดเอง
                </Text>
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <QrCountdown expiresAt={new Date(charge.expires_at).getTime()} />
          </div>

          <div style={{ marginTop: 8 }}>
            <Text strong style={{ fontSize: 22, color: "#1677ff" }}>
              ยอดชำระ {numberFormatTHB(charge.price_thb)} บาท
            </Text>
          </div>

          <div style={{ marginTop: 20 }}>
            <Button block icon={<ReloadOutlined />} loading={statusFetching} onClick={() => refetchStatus()}>
              ฉันชำระเงินแล้ว — ตรวจสอบสถานะ
            </Button>
          </div>

          {!hasRealQr && (
            <div style={{ marginTop: 12 }}>
              <Button icon={<ExportOutlined />} onClick={() => window.open(charge.qr_content, "_blank", "noopener")}>
                เปิดหน้าชำระเงิน
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <>
      <Card bordered={false} className="criclebox h-full tabs-mobile" style={{ marginBottom: 16 }}>
        <ManageParcelPageHeader title="ชำระเงิน" onBack={handleBack} />
      </Card>

      <Card bordered={false} className="criclebox h-full tabs-mobile">
        {/* 2026-09-08: maxWidth 1400 ให้เท่ากับหน้าเลือกแพ็กเกจ (CreditTopupPage.tsx) ตามที่ขอ — ใช้
         * อัตราส่วนคอลัมน์ 3fr:2fr เดียวกัน การ์ดขวา (QR) เลยได้ความกว้างเท่ากับการ์ด
         * "รายละเอียดคำสั่งซื้อ"/"ช่องทางชำระเงิน" ตอนเลือกแพ็กเกจพอดี */}
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "8px 8px 32px" }}>
          {!packageVersionId ? (
            <Result
              status="error"
              title="ไม่พบแพ็กเกจที่เลือก"
              subTitle="กรุณากลับไปเลือกแพ็กเกจใหม่อีกครั้ง"
              extra={
                <Button type="primary" onClick={() => navigate("/manage-credit/topup")}>
                  กลับไปเลือกแพ็กเกจ
                </Button>
              }
            />
          ) : (
            <CheckoutLayout>
              {renderOrderDetail()}
              {renderPaymentPanel()}
            </CheckoutLayout>
          )}
        </div>
      </Card>
    </>
  );
};

export default CreditTopupCheckoutPage;
